import {
  Button,
  Icon,
  InlineMessage,
  InputField,
  Link,
  Typography,
} from "@saviynt/design-system";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

interface PasswordStepProps {
  username: string;
  onUsernameChange: (value: string) => void;
  onSubmit: (password: string) => void;
  onForgotPassword: () => void;
  isLoading: boolean;
  error?: string;
}

export default function PasswordStep({
  username,
  onUsernameChange,
  onSubmit,
  onForgotPassword,
  isLoading,
  error,
}: PasswordStepProps) {
  const intl = useIntl();
  const [visible, setVisible] = useState(false);
  const [password, setPassword] = useState("");
  const [usernameCritical, setUsernameCritical] = useState(false);
  const [passwordCritical, setPasswordCritical] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const isUsernameEmpty = !username.trim();
    const isPasswordEmpty = !password.trim();

    if (isUsernameEmpty) setUsernameCritical(true);
    if (isPasswordEmpty) setPasswordCritical(true);
    if (isUsernameEmpty || isPasswordEmpty) return;

    onSubmit(password);
  };

  const handleUsernameChange = (value: string) => {
    setUsernameCritical(false);
    onUsernameChange(value);
  };

  const handlePasswordChange = (value: string) => {
    setPasswordCritical(false);
    setPassword(value);
  };

  return (
    <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
      <InputField
        id="username-display"
        name="username-display"
        label={
          <Typography kind="label" htmlFor="username-display">
            <FormattedMessage
              id="GUK5WY"
              defaultMessage="Username"
              description="Username field label"
            />
          </Typography>
        }
        type="text"
        value={username}
        setValue={handleUsernameChange}
        isCritical={usernameCritical}
        setIsCritical={setUsernameCritical}
        CriticalHelperText={
          <InlineMessage
            text={intl.formatMessage({
              id: "L3hsRD",
              defaultMessage: "Username is required",
              description: "Username required error message",
            })}
            colorTheme="critical"
            size="small"
            leftIcon={<Icon kind="AlertCriticalFilled" />}
          />
        }
        placeholder={intl.formatMessage({
          id: "r3CKXe",
          defaultMessage: "Enter your username",
          description: "Username field placeholder",
        })}
      />
      <InputField
        id="password"
        name="password"
        label={
          <Typography kind="label" htmlFor="password">
            <FormattedMessage
              id="GBq/YG"
              defaultMessage="Password"
              description="Password field label"
            />
          </Typography>
        }
        type={visible ? "text" : "password"}
        value={password}
        setValue={handlePasswordChange}
        isCritical={passwordCritical}
        setIsCritical={setPasswordCritical}
        CriticalHelperText={
          <InlineMessage
            text={intl.formatMessage({
              id: "b8CGG9",
              defaultMessage: "Password is required",
              description: "Password required error message",
            })}
            colorTheme="critical"
            size="small"
            leftIcon={<Icon kind="AlertCriticalFilled" />}
          />
        }
        placeholder={intl.formatMessage({
          id: "vzroba",
          defaultMessage: "Enter your password",
          description: "Password field placeholder",
        })}
        dataTestId="login-password-input"
        suffixButtonType="password"
        suffixIsToggle={visible}
        suffixOnClick={() => setVisible(!visible)}
      />
      <div className="flex items-center justify-between">
        <Link
          text={intl.formatMessage({
            id: "dbqfSg",
            defaultMessage: "Forgot password?",
            description: "Forgot password link",
          })}
          kind="button"
          onClick={onForgotPassword}
          dataTestId="login-forgotPassword-link"
        />
      </div>
      {error && (
        <InlineMessage
          text={error}
          colorTheme="critical"
          kind="background"
          size="small"
          leftIcon={<Icon kind="AlertCriticalFilled" />}
        />
      )}
      <Button
        type="submit"
        size="large"
        className="w-full justify-center"
        isDisabled={isLoading}
        dataTestId="login-signIn-btn"
      >
        <FormattedMessage
          id="3s73DJ"
          defaultMessage="Continue"
          description="Continue button text"
        />
      </Button>
    </form>
  );
}
